import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import SEO from "@/components/SEO";
import { collectionMethodLabelFor } from "@/lib/albumPresentation";
import AnalyticsDrilldownDrawer from "@/components/admin/AnalyticsDrilldownDrawer";
import {
  RefreshCw, Users, BookOpen, Boxes, Copy, Award, TrendingUp, AlertTriangle,
  ArrowLeft,
} from "lucide-react";

/**
 * C4 · Admin Collector Metrics Dashboard.
 *
 * Read-only. Admin-only. Consumes the permanent C3 endpoint:
 *   GET /api/admin/collector-analytics/summary?window=(all|today|7d|30d)
 *
 * No mutations. No drilldowns (C5). Matches existing admin visual language
 * (rounded-2xl · border-2 · ink border · shadow · CollectorUp red accents).
 */

const WINDOWS = [
  ["all", "All time"],
  ["today", "Today"],
  ["7d", "Last 7 days"],
  ["30d", "Last 30 days"],
];

function fmt(n) {
  if (n == null || Number.isNaN(n)) return "—";
  return Number(n).toLocaleString("en-US");
}

function pct(v) {
  if (v == null || Number.isNaN(v)) return "—";
  return `${(v * 100).toFixed(1)}%`;
}

// C4.1 · Human-readable label helpers. Never show raw enum values in the UI.
const SIGNUP_SOURCE_LABELS = {
  SELF_REGISTERED: "Self Registered",
  "social.google": "Google (OAuth)",
  "social.apple": "Apple (OAuth)",
  cx_gate_test: "Checkout Gate Test",
  UNKNOWN: "Unknown",
  unknown: "Unknown",
};

function humanizeSignupSource(key) {
  if (!key) return "Unknown";
  if (SIGNUP_SOURCE_LABELS[key]) return SIGNUP_SOURCE_LABELS[key];
  // Fallback: `some.thing_here` → `Some Thing Here`.
  return String(key)
    .replace(/[._]/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

function humanizeCollectionMethod(key) {
  if (!key) return "Unknown";
  // Delegate to the canonical album-presenter map to avoid duplication.
  const label = collectionMethodLabelFor({ collection_method: key });
  if (label) return label;
  return humanizeSignupSource(key); // fallback
}

function humanizeStageLabel(stage) {
  return String(stage || "")
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" ");
}

function KPI({ label, value, hint, Icon, testid, onDrilldown, drilldownMetric }) {
  const clickable = !!onDrilldown && !!drilldownMetric;
  const Wrapper = clickable ? "button" : "div";
  return (
    <Wrapper
      type={clickable ? "button" : undefined}
      onClick={clickable ? () => onDrilldown(drilldownMetric) : undefined}
      className={`text-left rounded-2xl border-2 border-[var(--ink)] bg-white p-4 shadow-[3px_3px_0_var(--ink)] ${clickable ? "cursor-pointer hover:-translate-y-0.5 hover:bg-[var(--panini-yellow-soft)] transition" : ""}`}
      data-testid={testid}
      data-drilldown={drilldownMetric || undefined}
    >
      <div className="flex items-start justify-between">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--smoke)]">
          {label}
        </div>
        {Icon && <Icon size={16} className="text-[var(--panini-red)]" />}
      </div>
      <div className="font-display text-3xl mt-2" data-testid={`${testid}-value`}>
        {fmt(value)}
      </div>
      {hint && (
        <div className="text-xs text-[var(--smoke)] mt-1" data-testid={`${testid}-hint`}>
          {hint}
        </div>
      )}
    </Wrapper>
  );
}

function SectionHeader({ Icon, label, title, subtitle }) {
  return (
    <div className="flex items-center gap-3">
      {Icon && (
        <div className="rounded-xl border-2 border-[var(--ink)] p-2 shadow-[3px_3px_0_var(--ink)] bg-white">
          <Icon size={20} className="text-[var(--panini-red)]" />
        </div>
      )}
      <div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--panini-red)]">
          {label}
        </div>
        <h2 className="font-display text-lg md:text-xl">{title}</h2>
        {subtitle && (
          <p className="text-xs text-[var(--smoke)] mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
  );
}

function AdoptionCard({ stage, count, pctOfAccounts, onDrilldown, drilldownMetric }) {
  const label = humanizeStageLabel(stage);
  const clickable = !!onDrilldown && !!drilldownMetric;
  const Wrapper = clickable ? "button" : "div";
  return (
    <Wrapper
      type={clickable ? "button" : undefined}
      onClick={clickable ? () => onDrilldown(drilldownMetric) : undefined}
      className={`text-left rounded-2xl border-2 border-[var(--ink)] bg-white p-3 md:p-4 shadow-[3px_3px_0_var(--ink)] ${clickable ? "cursor-pointer hover:-translate-y-0.5 hover:bg-[var(--panini-yellow-soft)] transition" : ""}`}
      data-testid={`adoption-card-${stage}`}
      data-drilldown={drilldownMetric || undefined}
    >
      <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--smoke)] leading-tight">
        {label}
      </div>
      <div
        className="font-display text-2xl md:text-3xl mt-1.5"
        data-testid={`adoption-card-${stage}-count`}
      >
        {fmt(count)}
      </div>
      <div
        className="text-xs text-[var(--panini-red)] font-bold mt-0.5"
        data-testid={`adoption-card-${stage}-pct`}
      >
        {pct(pctOfAccounts)} of accounts
      </div>
    </Wrapper>
  );
}

// Static mapping from adoption stage → drilldown metric. Only stages
// whose count reconciles with a drilldown are clickable. See PRD C5.
const ADOPTION_STAGE_TO_METRIC = {
  ACCOUNT_CREATED: "active_accounts",
  FIRST_ALBUM_ADDED: "album_owners",
  TRACKING_USED: "tracking_users",
  COLLECTORS_WITH_COMPLETED_ALBUM: "completed_album_collectors",
};

function Table({ rows, testid }) {
  if (!rows || rows.length === 0) {
    return (
      <div
        className="text-sm text-[var(--smoke)] italic py-3"
        data-testid={`${testid}-empty`}
      >
        No data yet.
      </div>
    );
  }
  return (
    <table className="w-full text-sm" data-testid={testid}>
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k} className="border-t border-neutral-100">
            <td className="py-1.5 text-[var(--smoke)]">{k}</td>
            <td className="py-1.5 text-right font-mono">{fmt(v)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Card({ children, className = "", testid }) {
  return (
    <div
      className={`rounded-2xl border-2 border-[var(--ink)] bg-white p-4 md:p-5 shadow-[3px_3px_0_var(--ink)] ${className}`}
      data-testid={testid}
    >
      {children}
    </div>
  );
}

export default function AdminCollectorAnalytics() {
  const [window, setWindow] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  // C5 · drilldown drawer state
  const [drilldownMetric, setDrilldownMetric] = useState(null);
  const openDrilldown = useCallback((metric) => setDrilldownMetric(metric), []);
  const closeDrilldown = useCallback(() => setDrilldownMetric(null), []);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await api.get(
        `/admin/collector-analytics/summary?window=${window}`
      );
      setData(r.data);
    } catch (e) {
      setErr(
        formatApiError(e?.response?.data?.detail) ||
          "Failed to load collector analytics."
      );
    } finally {
      setLoading(false);
    }
  }, [window]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div
      className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto"
      data-testid="admin-collector-analytics"
    >
      <SEO title="Collector Analytics · Admin · CollectorUp" />

      {/* Back to Admin Dashboard */}
      <div>
        <Link
          to="/admin"
          data-testid="back-to-admin-dashboard"
          className="inline-flex items-center gap-1.5 rounded-full border-2 border-[var(--ink)] bg-white px-3 py-1.5 text-xs font-bold uppercase tracking-widest hover:bg-[var(--panini-yellow-soft)] shadow-[3px_3px_0_var(--ink)] transition"
        >
          <ArrowLeft size={14} /> Back to Admin Dashboard
        </Link>
      </div>

      {/* Header + controls */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--panini-red)]">
            Ecosystem
          </div>
          <h1
            className="font-display text-3xl"
            data-testid="collector-analytics-title"
          >
            Collector Analytics
          </h1>
          <p className="text-xs text-[var(--smoke)] mt-0.5 max-w-2xl">
            Aggregate, read-only view of the collector ecosystem. Counts only —
            no personal data. Time-window controls affect new accounts, albums
            added and claimed certificates. Progress &amp; import totals are
            all-time.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <label className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">
            Window
          </label>
          <select
            value={window}
            onChange={(e) => setWindow(e.target.value)}
            className="border-2 border-[var(--ink)] rounded-full px-3 py-1 text-sm bg-white"
            data-testid="collector-analytics-window"
            disabled={loading}
          >
            {WINDOWS.map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={load}
            className="rounded-full border-2 border-[var(--ink)] px-3 py-1 text-sm flex items-center gap-1 bg-white hover:bg-neutral-50 disabled:opacity-50"
            data-testid="collector-analytics-refresh"
            disabled={loading}
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error */}
      {err && (
        <div
          className="rounded-2xl border-2 border-red-600 bg-red-50 p-4 text-sm text-red-800 flex items-start gap-2"
          data-testid="collector-analytics-error"
        >
          <AlertTriangle size={16} className="mt-0.5" />
          <div>
            <div className="font-bold">Could not load analytics</div>
            <div className="mt-1">{err}</div>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && !data && (
        <div
          className="rounded-2xl border-2 border-[var(--ink)] bg-white p-6 text-sm text-[var(--smoke)] shadow-[3px_3px_0_var(--ink)]"
          data-testid="collector-analytics-loading"
        >
          Loading collector ecosystem metrics…
        </div>
      )}

      {/* Data */}
      {data && (
        <>
          {/* 1. Hero KPI cards */}
          <section
            className="grid grid-cols-2 md:grid-cols-5 gap-3"
            data-testid="section-kpi-hero"
          >
            <KPI
              testid="kpi-active-accounts"
              label="Active accounts"
              value={data.accounts?.active_accounts}
              Icon={Users}
              hint={`+${fmt(data.accounts?.new_accounts?.["30d"])} in last 30d`}
              onDrilldown={openDrilldown}
              drilldownMetric="active_accounts"
            />
            <KPI
              testid="kpi-albums-tracked"
              label="Albums tracked"
              value={data.albums?.total_album_instances}
              Icon={BookOpen}
              hint={`${fmt(data.albums?.unique_owners)} unique owners`}
            />
            <KPI
              testid="kpi-tracking-users"
              label="Tracking users"
              value={data.progress?.tracking_users}
              Icon={Boxes}
              hint={`${fmt(data.progress?.total_sticker_inventory_rows)} inventory rows`}
              onDrilldown={openDrilldown}
              drilldownMetric="tracking_users"
            />
            <KPI
              testid="kpi-completed-albums"
              label="Completed albums"
              value={data.progress?.completed_albums}
              Icon={TrendingUp}
              hint={
                data.progress?.tracked_album_instances > 0
                  ? `${fmt(data.progress?.completed_tracked_album_instances)} of ${fmt(data.progress?.tracked_album_instances)} tracked (${pct(data.progress?.completion_rate_among_tracked)})`
                  : "No tracked albums yet"
              }
            />
            <KPI
              testid="kpi-certificates-claimed"
              label="Certificates claimed"
              value={data.certificates?.claimed?.total}
              Icon={Award}
              hint={`${fmt(data.certificates?.claimed?.["30d"])} in last 30d`}
            />
          </section>

          {/* 2. Adoption snapshot — compact KPI cards */}
          <section className="space-y-3" data-testid="section-funnel">
            <SectionHeader
              Icon={TrendingUp}
              label="Adoption"
              title="Collector adoption snapshot"
              subtitle="Current-state adoption — not chronological. Each card shows how many collectors reached that stage today."
            />
            <div
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3"
              data-testid="adoption-grid"
            >
              {(data.funnel?.stages || []).map((s) => (
                <AdoptionCard
                  key={s.stage}
                  stage={s.stage}
                  count={s.count}
                  pctOfAccounts={s.pct_of_account_holders}
                  onDrilldown={openDrilldown}
                  drilldownMetric={ADOPTION_STAGE_TO_METRIC[s.stage] || null}
                />
              ))}
              {(!data.funnel?.stages || data.funnel.stages.length === 0) && (
                <div className="text-sm text-[var(--smoke)] italic col-span-full">
                  No adoption data available.
                </div>
              )}
            </div>
          </section>

          {/* 3. Accounts + Albums row */}
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Accounts */}
            <Card testid="section-accounts">
              <SectionHeader
                Icon={Users}
                label="Accounts"
                title="Collector accounts"
                subtitle="Public/private, profile completion, sign-up source."
              />
              <div className="mt-3 grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => openDrilldown("public_profiles")}
                  className="text-left rounded-xl border border-[var(--stone)] p-3 cursor-pointer hover:bg-[var(--panini-yellow-soft)] hover:border-[var(--ink)] transition"
                  data-testid="accounts-public-card"
                  data-drilldown="public_profiles"
                >
                  <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">
                    Public profiles
                  </div>
                  <div
                    className="font-display text-2xl mt-1"
                    data-testid="accounts-public"
                  >
                    {fmt(data.accounts?.public_profiles)}
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => openDrilldown("private_profiles")}
                  className="text-left rounded-xl border border-[var(--stone)] p-3 cursor-pointer hover:bg-[var(--panini-yellow-soft)] hover:border-[var(--ink)] transition"
                  data-testid="accounts-private-card"
                  data-drilldown="private_profiles"
                >
                  <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">
                    Private profiles
                  </div>
                  <div
                    className="font-display text-2xl mt-1"
                    data-testid="accounts-private"
                  >
                    {fmt(data.accounts?.private_profiles)}
                  </div>
                </button>
              </div>
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)] mb-1">
                  Snapshot
                </div>
                <Table
                  testid="accounts-table"
                  rows={[
                    ["Total collector rows", data.accounts?.total_collector_rows],
                    ["Non-account rows (imports)", data.accounts?.non_account_rows],
                    ["Profile started", data.accounts?.profile_started],
                    ["Profile completed", data.accounts?.profile_completed],
                    ["New today", data.accounts?.new_accounts?.today],
                    ["New last 7 days", data.accounts?.new_accounts?.["7d"]],
                    ["New last 30 days", data.accounts?.new_accounts?.["30d"]],
                  ]}
                />
                <div className="mt-2 text-xs text-[var(--smoke)]">
                  Profile completion rate:{" "}
                  <span
                    className="font-bold text-[var(--ink)]"
                    data-testid="accounts-completion-rate"
                  >
                    {pct(data.accounts?.profile_completion_rate)}
                  </span>
                </div>
              </div>
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)] mb-1">
                  Sign-up source
                </div>
                <Table
                  testid="signup-source-table"
                  rows={Object.entries(data.accounts?.signup_source || {})
                    .sort((a, b) => (b[1] || 0) - (a[1] || 0))
                    .map(([k, v]) => [humanizeSignupSource(k), v])}
                />
              </div>
            </Card>

            {/* Albums */}
            <Card testid="section-albums">
              <SectionHeader
                Icon={BookOpen}
                label="Albums"
                title="Album ownership &amp; progression"
                subtitle="How many albums exist, how many owners hold them, growth over time."
              />
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-[var(--stone)] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">
                    Avg albums / owner
                  </div>
                  <div
                    className="font-display text-2xl mt-1"
                    data-testid="albums-avg"
                  >
                    {data.albums?.average_albums_per_owner ?? "—"}
                  </div>
                </div>
                <div className="rounded-xl border border-[var(--stone)] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">
                    Median albums / owner
                  </div>
                  <div
                    className="font-display text-2xl mt-1"
                    data-testid="albums-median"
                  >
                    {data.albums?.median_albums_per_owner ?? "—"}
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)] mb-1">
                  Snapshot
                </div>
                <Table
                  testid="albums-table"
                  rows={[
                    ["Total album instances", data.albums?.total_album_instances],
                    ["Unique owners", data.albums?.unique_owners],
                    ["Collectors with 0 albums", data.albums?.collectors_with_zero_albums],
                    ["Added today", data.albums?.albums_added?.today],
                    ["Added last 7 days", data.albums?.albums_added?.["7d"]],
                    ["Added last 30 days", data.albums?.albums_added?.["30d"]],
                  ]}
                />
              </div>
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)] mb-1">
                  Collection method
                </div>
                <Table
                  testid="collection-method-table"
                  rows={Object.entries(data.albums?.by_collection_method || {})
                    .sort((a, b) => (b[1] || 0) - (a[1] || 0))
                    .map(([k, v]) => [humanizeCollectionMethod(k), v])}
                />
              </div>
            </Card>
          </section>

          {/* 4. Missing / Duplicate usage */}
          <section data-testid="section-imports">
            <SectionHeader
              Icon={Copy}
              label="Missing &amp; Duplicates"
              title="Tracking activity"
              subtitle="How many collectors are actively logging missing stickers and extras."
            />
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card testid="card-missing">
                <div className="text-sm font-bold">Missing</div>
                <div className="mt-2 grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => openDrilldown("missing_users")}
                    className="text-left rounded-lg cursor-pointer hover:bg-[var(--panini-yellow-soft)] transition p-1 -m-1"
                    data-testid="missing-collectors-card"
                    data-drilldown="missing_users"
                  >
                    <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">
                      Unique collectors
                    </div>
                    <div
                      className="font-display text-2xl mt-1"
                      data-testid="missing-collectors"
                    >
                      {fmt(data.progress?.missing?.unique_collectors)}
                    </div>
                  </button>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">
                      Total stickers marked
                    </div>
                    <div
                      className="font-display text-2xl mt-1"
                      data-testid="missing-total"
                    >
                      {fmt(data.progress?.missing?.total_marked_missing)}
                    </div>
                  </div>
                </div>
                <div className="mt-3 text-xs text-[var(--smoke)]">
                  <span data-testid="missing-import-users">
                    {fmt(data.imports?.missing_import_users)}
                  </span>{" "}
                  collectors used the missing-list importer (
                  <span data-testid="missing-import-count">
                    {fmt(data.imports?.missing_import_count)}
                  </span>{" "}
                  imports total).
                </div>
              </Card>
              <Card testid="card-duplicates">
                <div className="text-sm font-bold">Duplicates</div>
                <div className="mt-2 grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => openDrilldown("duplicate_users")}
                    className="text-left rounded-lg cursor-pointer hover:bg-[var(--panini-yellow-soft)] transition p-1 -m-1"
                    data-testid="duplicates-collectors-card"
                    data-drilldown="duplicate_users"
                  >
                    <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">
                      Unique collectors
                    </div>
                    <div
                      className="font-display text-2xl mt-1"
                      data-testid="duplicates-collectors"
                    >
                      {fmt(data.progress?.duplicates?.unique_collectors)}
                    </div>
                  </button>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">
                      Total duplicate quantity
                    </div>
                    <div
                      className="font-display text-2xl mt-1"
                      data-testid="duplicates-total"
                    >
                      {fmt(data.progress?.duplicates?.total_duplicate_quantity)}
                    </div>
                  </div>
                </div>
                <div className="mt-3 text-xs text-[var(--smoke)]">
                  <span data-testid="duplicate-import-users">
                    {fmt(data.imports?.duplicate_import_users)}
                  </span>{" "}
                  collectors used the duplicate-list importer (
                  <span data-testid="duplicate-import-count">
                    {fmt(data.imports?.duplicate_import_count)}
                  </span>{" "}
                  imports total).
                </div>
              </Card>
            </div>
            <div className="mt-3 text-xs text-[var(--smoke)]">
              Collectors doing <span className="font-bold text-[var(--ink)]" data-testid="both-missing-and-duplicate">
                {fmt(data.progress?.both_missing_and_duplicate_users)}
              </span> both missing &amp; duplicate tracking ·{" "}
              <span className="font-bold text-[var(--ink)]" data-testid="both-import-users">
                {fmt(data.imports?.both_import_users)}
              </span>{" "}
              used both importers.
            </div>
          </section>

          {/* 5. Certificates */}
          <section data-testid="section-certificates">
            <SectionHeader
              Icon={Award}
              label="Certificates"
              title="Certificate ownership &amp; claims"
              subtitle="How certificate holders overlap with the collector ecosystem."
            />
            <Card className="mt-3" testid="certificates-card">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="rounded-xl border border-[var(--stone)] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">
                    Total certificates
                  </div>
                  <div className="font-display text-2xl mt-1" data-testid="cert-total">
                    {fmt(data.certificates?.total_certificates)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => openDrilldown("certificate_holders")}
                  className="text-left rounded-xl border border-[var(--stone)] p-3 cursor-pointer hover:bg-[var(--panini-yellow-soft)] hover:border-[var(--ink)] transition"
                  data-testid="cert-holders-card"
                  data-drilldown="certificate_holders"
                >
                  <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">
                    Unique holders (linked)
                  </div>
                  <div className="font-display text-2xl mt-1" data-testid="cert-holders">
                    {fmt(data.certificates?.unique_collectors_with_certificate)}
                  </div>
                  <div className="text-[10px] text-[var(--smoke)] mt-1">
                    via approved claim
                  </div>
                </button>
                <div className="rounded-xl border border-[var(--stone)] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">
                    With profile
                  </div>
                  <div className="font-display text-2xl mt-1" data-testid="cert-with-profile">
                    {fmt(data.certificates?.certified_with_profile)}
                  </div>
                </div>
                <div className="rounded-xl border border-[var(--stone)] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">
                    With album
                  </div>
                  <div className="font-display text-2xl mt-1" data-testid="cert-with-album">
                    {fmt(data.certificates?.certified_with_album)}
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)] mb-1">
                  Claim activity
                </div>
                <Table
                  testid="claims-table"
                  rows={[
                    ["Total approved claims", data.certificates?.claimed?.total],
                    ["Claimed today", data.certificates?.claimed?.today],
                    ["Claimed last 7 days", data.certificates?.claimed?.["7d"]],
                    ["Claimed last 30 days", data.certificates?.claimed?.["30d"]],
                    ["Unlinked / legacy approved claims",
                      data.certificates?.unlinked_or_legacy_approved_claims],
                  ]}
                />
                <div className="mt-2 text-[10px] text-[var(--smoke)]">
                  Holders are derived from approved <code>certificate_claims</code>
                  {" "}rows (canonical linkage). Unlinked/legacy claims have no
                  {" "}collector id and are counted separately — never inferred
                  {" "}by name or email.
                </div>
              </div>
            </Card>
          </section>

          {/* Footer meta */}
          <div
            className="text-[10px] uppercase tracking-widest text-[var(--smoke)] text-right"
            data-testid="collector-analytics-meta"
          >
            Window: {data.window} · generated in {data.generated_in_ms}ms
          </div>
        </>
      )}

      {/* C5 · Drilldown drawer (portal-style overlay) */}
      <AnalyticsDrilldownDrawer
        open={!!drilldownMetric}
        metricKey={drilldownMetric}
        onClose={closeDrilldown}
      />
    </div>
  );
}
